import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { EventBus } from '../core/EventBus';
import { TravelMode, TravelPhase } from '../systems/TravelTypes';

/**
 * DOM-based travel control panel with step-by-step UX.
 * All interaction goes through EventBus — no direct system references.
 */
export class TravelPanel implements GameModule {
  readonly name = 'TravelPanel';

  private _events!: EventBus;
  private _root!: HTMLDivElement;
  private _phase = TravelPhase.Idle;
  private _hasStart = false;
  private _hasEnd = false;
  private _startQ = 0;
  private _startR = 0;

  private _statusEl!: HTMLSpanElement;
  private _coordsEl!: HTMLSpanElement;
  private _modeSelect!: HTMLSelectElement;
  private _startBtn!: HTMLButtonElement;
  private _goBtn!: HTMLButtonElement;
  private _cancelBtn!: HTMLButtonElement;
  private _newBtn!: HTMLButtonElement;
  private _gearBtn!: HTMLButtonElement;
  private _exitViewBtn!: HTMLButtonElement;
  private _inFlightView = false;

  init(engine: Engine): void {
    this._events = engine.events;
    this._buildDOM();

    this._events.on<{ phase: TravelPhase }>('travel:phase-changed', (d) => {
      this._phase = d.phase;
      if (d.phase === TravelPhase.Idle) { this._hasStart = false; this._hasEnd = false; }
      this._sync();
    });
    this._events.on<{ q: number; r: number }>('travel:start-set', (d) => {
      this._hasStart = true;
      this._startQ = d.q; this._startR = d.r;
      this._coordsEl.textContent = `S(${d.q},${d.r})`;
      this._sync();
    });
    this._events.on<{ q: number; r: number }>('travel:end-set', (d) => {
      this._hasEnd = true;
      this._coordsEl.textContent = `S(${this._startQ},${this._startR}) → Z(${d.q},${d.r})`;
      this._sync();
    });
    this._events.on<{ mode: number }>('camera:mode-changed', (d) => {
      this._inFlightView = d.mode === 1;
      this._sync();
    });
    this._events.on<{ message: string }>('travel:error', (d) => {
      this._statusEl.textContent = `Fehler: ${d.message}`;
      this._statusEl.style.color = '#f88';
      setTimeout(() => { this._statusEl.style.color = ''; this._sync(); }, 3000);
    });
  }

  update(_dt: number): void {}
  dispose(): void { this._root.remove(); }

  private _buildDOM(): void {
    this._root = document.createElement('div');
    Object.assign(this._root.style, {
      position: 'fixed', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(10,12,20,0.88)', color: '#ccc', fontFamily: 'monospace',
      fontSize: 'clamp(11px, 2.6vw, 13px)', padding: 'clamp(8px,1.5vw,10px) clamp(10px,2vw,16px)',
      borderRadius: '8px',
      zIndex: '9998', display: 'flex', gap: 'clamp(6px,1.5vw,10px)', alignItems: 'center',
      border: '1px solid rgba(80,120,255,0.3)', whiteSpace: 'nowrap',
    });

    this._gearBtn = this._btn('\u2699', '#2a3050', () => {
      this._events.emit('travel:toggle-settings', null);
    });
    this._gearBtn.style.fontSize = '16px';
    this._gearBtn.style.padding = '3px 8px';

    this._modeSelect = this._select([['Normal', '0'], ['Aufklärung', '1'], ['Warp', '2']]);
    this._startBtn = this._btn('Reise starten', '#2a4060', () => {
      this._events.emit('travel:select-start', null);
    });
    this._goBtn = this._btn('Los!', '#1a5030', () => {
      const mode = Number(this._modeSelect.value) as TravelMode;
      this._events.emit('travel:go', { mode });
    });
    this._cancelBtn = this._btn('Abbrechen', '#502a2a', () => {
      this._events.emit('travel:cancel', null);
    });
    this._newBtn = this._btn('Neue Reise', '#2a4060', () => {
      this._events.emit('travel:cancel', null);
    });
    this._exitViewBtn = this._btn('Zur Karte', '#2a3060', () => {
      this._events.emit('fleet:exit-flightview', null);
    });

    this._statusEl = document.createElement('span');
    this._statusEl.style.minWidth = '110px';
    this._coordsEl = document.createElement('span');
    this._coordsEl.style.color = '#6af';

    this._root.appendChild(this._gearBtn);
    this._root.appendChild(this._modeSelect);
    this._root.appendChild(this._startBtn);
    this._root.appendChild(this._statusEl);
    this._root.appendChild(this._coordsEl);
    this._root.appendChild(this._goBtn);
    this._root.appendChild(this._cancelBtn);
    this._root.appendChild(this._newBtn);
    this._root.appendChild(this._exitViewBtn);

    document.body.appendChild(this._root);
    this._sync();
  }

  private _sync(): void {
    const p = this._phase;
    const idle = p === TravelPhase.Idle;
    const arrived = p === TravelPhase.Arrived;
    const selectingStart = p === TravelPhase.SelectingStart;
    const selectingEnd = p === TravelPhase.SelectingEnd;
    const pathfinding = p === TravelPhase.Pathfinding;
    const traveling = p === TravelPhase.Traveling;
    const readyToGo = selectingEnd && this._hasStart && this._hasEnd;

    this._gearBtn.style.display = idle || readyToGo ? '' : 'none';
    this._modeSelect.style.display = idle || readyToGo ? '' : 'none';
    this._startBtn.style.display = idle ? '' : 'none';
    this._goBtn.style.display = readyToGo ? '' : 'none';
    this._cancelBtn.style.display = !idle && !arrived ? '' : 'none';
    this._newBtn.style.display = arrived ? '' : 'none';
    this._exitViewBtn.style.display = this._inFlightView ? '' : 'none';

    if (idle) {
      this._statusEl.textContent = '';
      this._coordsEl.textContent = '';
    } else if (selectingStart) {
      this._statusEl.textContent = 'Start wählen…';
    } else if (selectingEnd && !this._hasEnd) {
      this._statusEl.textContent = 'Ziel wählen…';
    } else if (readyToGo) {
      this._statusEl.textContent = 'Route fertig —';
    } else if (pathfinding) {
      this._statusEl.textContent = 'Berechne…';
    } else if (traveling) {
      this._statusEl.textContent = 'Unterwegs…';
    } else if (arrived) {
      this._statusEl.textContent = 'Angekommen!';
      this._statusEl.style.color = '#4f8';
      setTimeout(() => { this._statusEl.style.color = ''; }, 2000);
    }
  }

  private _btn(text: string, bg: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = text;
    Object.assign(b.style, {
      background: bg, color: '#cdf', border: '1px solid #556',
      borderRadius: '4px', padding: 'clamp(6px,1.5vw,8px) clamp(10px,2vw,12px)', cursor: 'pointer',
      fontSize: 'clamp(10px, 2.4vw, 12px)', fontFamily: 'monospace', minHeight: '44px',
    });
    b.addEventListener('click', onClick);
    return b;
  }

  private _select(options: string[][]): HTMLSelectElement {
    const sel = document.createElement('select');
    Object.assign(sel.style, {
      background: '#1a1e2e', color: '#aaa', border: '1px solid #444',
      borderRadius: '4px', padding: 'clamp(6px,1.5vw,8px)', fontSize: 'clamp(10px,2.4vw,12px)',
      fontFamily: 'monospace', minHeight: '44px',
    });
    for (const [label, val] of options) {
      const opt = document.createElement('option');
      opt.value = val; opt.textContent = label;
      sel.appendChild(opt);
    }
    return sel;
  }
}
